import { getNodeDataRecord, getValueNodeBounds, getValueSubtypeLabel } from '../graph/nodeDefs.js';
import type { GraphNode, ValueSubtype } from '../graph/nodeDefs.js';
import { hexToRgb, rgbToHex } from '../math/color.js';
import { createDragger, type DragFieldElement } from './controls/dragField.js';
import { createColorControl, createEditableColorField } from './colorField.js';
import {
  createNodeFieldRow as createRow,
  createNodeFieldSelect as createSelect,
  createNodeValueLabel,
  createValueSectionRow,
} from './nodeFieldEditorControls.js';
import type { NodeFieldRenderDeps as RenderDeps } from './nodeRenderers.js';
import { appendExposeAndName, appendOutputPort } from './valueFieldEditorShared.js';

const FLOAT_SUBTYPES: ValueSubtype[] = ['default', 'factor', 'angle', 'distance', 'pixels', 'percentage', 'em'];

export function renderFloatValueUI(node: GraphNode, container: HTMLElement, deps: RenderDeps): void {
  const data = getNodeDataRecord(node);
  appendExposeAndName(node, container, deps);

  const subtypeRow = createRow();
  subtypeRow.appendChild(createNodeValueLabel('Subtype'));
  const subtypeSelect = createSelect('100%');
  const currentSubtype = (data.subtype as ValueSubtype) || 'default';
  for (const st of FLOAT_SUBTYPES) {
    const opt = document.createElement('option');
    opt.value = st;
    opt.textContent = st;
    opt.selected = st === currentSubtype;
    subtypeSelect.appendChild(opt);
  }
  subtypeSelect.addEventListener('change', () => {
    deps.commands.setValueSubtype(node.id, subtypeSelect.value as ValueSubtype);
    deps.updateParams();
    deps.renderGraph();
    deps.scheduleRender();
  });
  subtypeRow.appendChild(subtypeSelect);
  container.appendChild(subtypeRow);

  const sectionLabel = data.exposed ? 'Default' : 'Value';
  container.appendChild(createValueSectionRow(sectionLabel));

  const bounds = getValueNodeBounds(data as any);
  const valueRow = createRow();
  valueRow.appendChild(createNodeValueLabel(getValueSubtypeLabel(currentSubtype as ValueSubtype)));
  const input = createDragger(
    Number(data.value0 ?? 0),
    bounds.min, bounds.max, 0.01,
    value => {
      deps.commands.setValueComponent(node.id, 0, value);
      deps.updateParams();
      deps.scheduleRender();
    },
    undefined,
    { showProgress: bounds.hasSlider, subtype: currentSubtype === 'default' ? undefined : currentSubtype },
  );
  input.style.width = '100%';
  valueRow.appendChild(input);
  container.appendChild(valueRow);

  ([['min', 'Min'], ['max', 'Max']] as const).forEach(([field, labelText]) => {
    const rangeRow = createRow();
    const label = document.createElement('label');
    label.className = 'value-range-label';
    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.checked = field === 'min' ? Boolean(data.hasMin) : Boolean(data.hasMax);
    toggle.addEventListener('mousedown', e => e.stopPropagation());
    toggle.addEventListener('change', () => {
      deps.commands.setValueRangeEnabled(node.id, field, toggle.checked);
      deps.updateParams();
      deps.renderGraph();
      deps.scheduleRender();
    });
    const text = document.createElement('span');
    text.className = 'value-range-text';
    text.textContent = labelText;
    label.appendChild(toggle);
    label.appendChild(text);
    rangeRow.appendChild(label);
    const rangeInput = createDragger(
      Number(data[field] ?? 0), Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY, 0.01,
      value => {
        deps.commands.setValueRangeField(node.id, field, value);
        deps.updateParams();
        deps.scheduleRender();
      },
      undefined,
      { subtype: currentSubtype === 'default' ? undefined : currentSubtype },
    );
    rangeInput.style.width = '100%';
    rangeRow.appendChild(rangeInput);
    container.appendChild(rangeRow);
  });

  appendOutputPort(node, 'float', container, deps);
}

export function renderVecValueUI(node: GraphNode, container: HTMLElement, deps: RenderDeps): void {
  const data = getNodeDataRecord(node);
  appendExposeAndName(node, container, deps);

  const sectionLabel = data.exposed ? 'Default' : 'Value';
  container.appendChild(createValueSectionRow(sectionLabel));

  const portType = node.type === 'Vector2' ? 'vec2' : node.type === 'Vector3' ? 'vec3' : 'vec4';
  const numComps = node.type === 'Vector2' ? 2 : node.type === 'Vector3' ? 3 : 4;
  const componentLabels = ['X', 'Y', 'Z', 'W'];
  for (let i = 0; i < numComps; i++) {
    const valueRow = createRow();
    valueRow.appendChild(createNodeValueLabel(componentLabels[i]));
    const input = createDragger(
      Number(data[`value${i}`] ?? 0), -1000, 1000, 0.01,
      value => {
        deps.commands.setValueComponent(node.id, i as 0 | 1 | 2 | 3, value);
        deps.updateParams();
        deps.scheduleRender();
      },
    );
    input.style.width = '100%';
    valueRow.appendChild(input);
    container.appendChild(valueRow);
  }

  appendOutputPort(node, portType, container, deps);
}

export function renderColorNodeUI(node: GraphNode, container: HTMLElement, deps: RenderDeps): void {
  const data = getNodeDataRecord(node);
  appendExposeAndName(node, container, deps);

  const sectionLabel = data.exposed ? 'Default' : 'Value';
  container.appendChild(createValueSectionRow(sectionLabel));

  const colorRow = createRow();
  const colorValue = rgbToHex(
    Number(data.value0 ?? 0),
    Number(data.value1 ?? 0),
    Number(data.value2 ?? 0),
  );
  let alphaInput: DragFieldElement | undefined;
  const colorField = createEditableColorField({
    value: colorValue,
    opacity: Number(data.value3 ?? 1),
    classNames: {
      field: 'value-color-field',
      swatchButton: 'value-color-swatch-button',
      swatch: 'value-color-swatch',
      valueInput: 'value-color-value',
    },
    inputAriaLabel: 'Hex color',
    stopMouseDownPropagation: true,
    onInputValue: value => {
      const [red, green, blue] = hexToRgb(value);
      deps.commands.setValueComponent(node.id, 0, red);
      deps.commands.setValueComponent(node.id, 1, green);
      deps.commands.setValueComponent(node.id, 2, blue);
      deps.updateParams();
      deps.scheduleRender();
    },
    onOpacityInput: value => {
      deps.commands.setValueComponent(node.id, 3, value);
      alphaInput?.setValue(value);
      deps.updateParams();
      deps.scheduleRender();
    },
  });

  alphaInput = createDragger(
    Number(data.value3 ?? 1), 0, 1, 0.01,
    value => {
      deps.commands.setValueComponent(node.id, 3, value);
      colorField.setOpacity(value);
      deps.updateParams();
      deps.scheduleRender();
    },
    undefined,
    { subtype: 'percentage' },
  );
  alphaInput.style.width = '56px';

  colorRow.appendChild(createColorControl({
    classNames: {
      container: 'value-inline-control value-inline-control--row',
      leading: 'value-inline-control value-inline-control--leading',
      label: 'value-channel-label',
      trailing: 'value-inline-control value-inline-control--alpha',
    },
    labelText: 'Color',
    colorField,
    trailingControl: alphaInput,
  }));
  container.appendChild(colorRow);

  appendOutputPort(node, 'vec4', container, deps);
}
